/**
 * Running a search.
 *
 * Two hooks, because the product has two search behaviours and conflating them
 * is what produces the "dumped the repository on the user" failure:
 *
 *   useInstantSearch — the top five while typing, however many matched.
 *   useFullSearch    — the ranked results page, 25 at a time, reachable to 500.
 *
 * THE CEILING IS NOT A SEARCH LIMIT. Every document the caller may see is
 * searched and ranked; 500 is how far into that ranking paging can go. When
 * more matched, the results still appear — the best of them — and the page
 * says the tail was cut so the reader knows to narrow rather than assuming
 * they have seen everything.
 *
 * Both hooks talk to Algolia directly with a key the backend signed. Nothing
 * here decides what may be found; the key already did, and it cannot be
 * widened from this side.
 */

import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import algoliasearch from 'algoliasearch/lite';
import { useSearchKey } from './useSearchKey';
import { buildParams } from './searchQuery';
import { pageMath, PAGE_SIZE } from './pagination';
import type { FacetCounts, FacetSelection, SearchHit, SearchKey, SearchResult } from './types';

export { PAGE_SIZE };

/** How many hits the type-ahead shows. */
export const INSTANT_HITS = 5;

/** Below this, a query is too short to mean anything. */
export const MIN_QUERY_LENGTH = 2;

interface AlgoliaResponse {
  hits: SearchHit[];
  nbHits: number;
  facets?: FacetCounts;
  processingTimeMS: number;
  query: string;
  exhaustiveNbHits?: boolean;
}

function indexFor(key: SearchKey) {
  return algoliasearch(key.appId, key.searchKey).initIndex(key.indexName);
}

interface RunInput {
  key: SearchKey;
  query: string;
  facets: FacetSelection;
  hitsPerPage: number;
  page: number;
  /** Apply the paging ceiling. False for the type-ahead, which shows five. */
  enforceCeiling: boolean;
  /** What the policy library adds: see QueryInput. */
  extraFacetFilters?: string[][];
  numericFilters?: string[];
}

export async function runSearch(input: RunInput): Promise<SearchResult> {
  const ceiling = input.enforceCeiling ? input.key.maxResults : Infinity;

  // Asking for a page past the ceiling would return rows the interface then
  // has to explain, so the request is clamped before it is sent.
  const clampedPage = input.enforceCeiling
    ? Math.min(input.page, Math.max(0, Math.ceil(ceiling / input.hitsPerPage) - 1))
    : input.page;

  const params = buildParams({
    query: input.query,
    facets: input.facets,
    boostDepartmentIds: input.key.boostDepartmentIds,
    hitsPerPage: input.hitsPerPage,
    page: clampedPage,
    extraFacetFilters: input.extraFacetFilters,
    numericFilters: input.numericFilters,
  });

  const response = (await indexFor(input.key).search(
    input.query,
    params,
  )) as unknown as AlgoliaResponse;

  const math = pageMath(response.nbHits, clampedPage, ceiling, input.hitsPerPage);

  return {
    hits: response.hits,
    totalHits: response.nbHits,
    exhaustive: response.exhaustiveNbHits !== false,
    facets: response.facets ?? {},
    processingTimeMs: response.processingTimeMS,
    query: response.query,
    tooMany: input.enforceCeiling && math.tooMany,
    reachableHits: math.reachableHits,
    lastPage: math.lastPage,
    page: clampedPage,
  };
}

/** Whether a query is worth sending. */
export function isSearchable(query: string, facets: FacetSelection = {}): boolean {
  if (query.trim().length >= MIN_QUERY_LENGTH) return true;
  // An empty box with a department chosen is a legitimate "show me everything
  // in Pharmacy", so facets alone are enough to run.
  return Object.values(facets).some(Boolean);
}

/** The type-ahead. Top five, no ceiling, no facets. */
export function useInstantSearch(query: string, enabled = true) {
  const { data: key } = useSearchKey(enabled);
  const trimmed = query.trim();

  return useQuery({
    queryKey: ['search-instant', key?.indexName, trimmed],
    enabled: enabled && Boolean(key) && trimmed.length >= MIN_QUERY_LENGTH,
    // The previous five stay on screen while the next five load, so the
    // dropdown does not blink empty between keystrokes.
    placeholderData: keepPreviousData,
    queryFn: () =>
      runSearch({
        key: key!,
        query: trimmed,
        facets: {},
        hitsPerPage: INSTANT_HITS,
        page: 0,
        enforceCeiling: false,
      }),
  });
}

/** The results page. Paged, and capped at the ceiling. */
export function useFullSearch(
  query: string,
  facets: FacetSelection,
  page = 0,
  enabled = true,
) {
  const { data: key, isLoading: keyLoading, error: keyError } = useSearchKey(enabled);
  const trimmed = query.trim();
  const searchable = isSearchable(trimmed, facets);

  // Structural, so an identical selection rebuilt on render does not look like
  // a new query and refetch.
  const facetKey = useMemo(() => JSON.stringify(facets), [facets]);

  const result = useQuery({
    queryKey: ['search-full', key?.indexName, trimmed, facetKey, page],
    enabled: enabled && Boolean(key) && searchable,
    placeholderData: keepPreviousData,
    queryFn: () =>
      runSearch({
        key: key!,
        query: trimmed,
        facets,
        hitsPerPage: PAGE_SIZE,
        page,
        enforceCeiling: true,
      }),
  });

  return { ...result, key, keyLoading, keyError, searchable };
}
